/**
 * WorkflowConfigTab.jsx — Module 2 SuperAdmin Edit-Org-modal tab.
 *
 * Placement: rendered inside SuperAdmin > More > Orgs > Edit Org > Workflow Config tab
 * (between "Permissions" and "Parser Config" tabs).
 *
 * Three grouped cards:
 *   1. Intake Hub      — flow toggles
 *   2. Payment Files   — schedule editor
 *   3. Validations     — DYNAMIC: rendered from /workflow-config/validation-rules.
 *                        Adding a new rule on the backend → it shows up here
 *                        automatically.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ListChecks,
  CalendarClock,
  ShieldCheck,
  Loader2,
  Save,
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Separator } from '../../components/ui/separator';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

import {
  fetchValidationRules,
  fetchWorkflowConfig,
  patchWorkflowConfig,
} from '../../api/workflowConfig';

const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const CADENCES = ['weekly', 'biweekly', 'monthly', 'custom_cron'];

const TIMEZONES = [
  'America/Chicago',
  'America/New_York',
  'America/Los_Angeles',
  'America/Denver',
  'UTC',
];

const GROUP_BY = ['property', 'vendor', 'none'];

// ─────────────────────────────────────────────────────────────────────
// Param renderers — driven by the registry's `kind` field.
// ─────────────────────────────────────────────────────────────────────
function ParamControl({ ruleKey, param, value, onChange }) {
  const testId = `wf-rule-${ruleKey}-${param.key}`;

  if (param.kind === 'bool') {
    return (
      <div className="flex items-center justify-between gap-3 py-1">
        <div className="space-y-0.5">
          <Label className="text-sm">{param.label}</Label>
          {param.help && (
            <p className="text-xs text-muted-foreground">{param.help}</p>
          )}
        </div>
        <Switch
          checked={!!value}
          onCheckedChange={(v) => onChange(v)}
          data-testid={testId}
        />
      </div>
    );
  }

  if (param.kind === 'number') {
    return (
      <div className="py-1">
        <Label className="text-sm">{param.label}</Label>
        <Input
          type="number"
          step={param.step ?? 'any'}
          min={param.min}
          max={param.max}
          value={value ?? ''}
          onChange={(e) =>
            onChange(e.target.value === '' ? null : Number(e.target.value))
          }
          className="rounded-none mt-1 max-w-[180px]"
          data-testid={testId}
        />
        {param.help && (
          <p className="text-xs text-muted-foreground mt-1">{param.help}</p>
        )}
      </div>
    );
  }

  if (param.kind === 'string') {
    return (
      <div className="py-1">
        <Label className="text-sm">{param.label}</Label>
        <Input
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-none mt-1"
          data-testid={testId}
        />
        {param.help && (
          <p className="text-xs text-muted-foreground mt-1">{param.help}</p>
        )}
      </div>
    );
  }

  if (param.kind === 'select') {
    return (
      <div className="py-1">
        <Label className="text-sm">{param.label}</Label>
        <Select value={String(value)} onValueChange={onChange}>
          <SelectTrigger
            className="rounded-none mt-1 max-w-[260px]"
            data-testid={testId}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(param.options ?? []).map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {param.help && (
          <p className="text-xs text-muted-foreground mt-1">{param.help}</p>
        )}
      </div>
    );
  }

  if (param.kind === 'multiselect') {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      const next = arr.includes(opt)
        ? arr.filter((x) => x !== opt)
        : [...arr, opt];
      onChange(next);
    };
    return (
      <div className="py-1">
        <Label className="text-sm">{param.label}</Label>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(param.options ?? []).map((o) => {
            const checked = arr.includes(o);
            return (
              <div
                key={o}
                className={`flex items-center gap-2 p-2 border rounded-none cursor-pointer transition-colors ${
                  checked
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/40'
                }`}
                onClick={() => toggle(o)}
                data-testid={`${testId}-${o}`}
              >
                <Checkbox checked={checked} />
                <span className="text-xs">{o}</span>
              </div>
            );
          })}
        </div>
        {param.help && (
          <p className="text-xs text-muted-foreground mt-2">{param.help}</p>
        )}
      </div>
    );
  }

  return (
    <div className="text-xs text-muted-foreground">
      Unsupported param kind: {param.kind}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Main tab
// ─────────────────────────────────────────────────────────────────────
export default function WorkflowConfigTab({ accountId, accountName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState([]);
  const [config, setConfig] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [rulesRes, configRes] = await Promise.all([
          fetchValidationRules(),
          fetchWorkflowConfig(accountId),
        ]);
        if (cancelled) return;
        setRules(rulesRes.rules || []);
        setConfig(configRes);
        setDirty(false);
      } catch (e) {
        toast.error('Failed to load workflow config');
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const update = (path, value) => {
    setConfig((prev) => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      let cur = next;
      for (let i = 0; i < path.length - 1; i += 1) {
        const k = path[i];
        cur[k] = cur[k] ?? {};
        cur = cur[k];
      }
      cur[path[path.length - 1]] = value;
      return next;
    });
    setDirty(true);
  };

  const updateRule = (ruleKey, field, value) => {
    setConfig((prev) => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      next.validations = next.validations || {};
      next.validations[ruleKey] = next.validations[ruleKey] || {
        enabled: true,
        params: {},
      };
      if (field === 'enabled') {
        next.validations[ruleKey].enabled = value;
      } else {
        next.validations[ruleKey].params = next.validations[ruleKey].params || {};
        next.validations[ruleKey].params[field] = value;
      }
      return next;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const patch = {
        include_parsed_bills_in_all_bills: config.include_parsed_bills_in_all_bills,
        intake_hub_enhancements_enabled: config.intake_hub_enhancements_enabled,
        auto_assign_bills_to_properties: config.auto_assign_bills_to_properties,
        payment_file_schedule_enabled: config.payment_file_schedule_enabled,
        payment_file_schedule: config.payment_file_schedule,
        validations: config.validations,
      };
      const updated = await patchWorkflowConfig(accountId, patch);
      setConfig(updated);
      setDirty(false);
      toast.success('Workflow config saved');
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || 'Save failed — check console'
      );
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const sortedRules = useMemo(
    () => [...rules].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [rules]
  );

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-12 text-muted-foreground"
        data-testid="wf-config-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading workflow config…
      </div>
    );
  }

  if (!config) {
    return (
      <div
        className="text-sm text-destructive py-8"
        data-testid="wf-config-error"
      >
        Could not load configuration.
      </div>
    );
  }

  const sched = config.payment_file_schedule || {};

  return (
    <div className="space-y-4" data-testid="wf-config-tab">
      <p className="text-xs text-muted-foreground">
        These settings control how data flows for{' '}
        <span className="font-medium">{accountName || 'this org'}</span>. Only
        BillPortal SuperAdmins flip these in v1.
      </p>

      {/* ─────── Intake Hub card ─────── */}
      <Card className="rounded-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ListChecks className="h-4 w-4" /> Intake Hub
          </CardTitle>
          <CardDescription className="text-xs">
            Gate the flow from Intake Hub → Bills / Payment Files.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleRow
            label="Include parsed bills in All Bills"
            help="When OFF, parsed bills stay in Intake Hub until manually promoted, even if validations pass."
            checked={config.include_parsed_bills_in_all_bills}
            onChange={(v) =>
              update(['include_parsed_bills_in_all_bills'], v)
            }
            testid="wf-config-include_parsed_bills_in_all_bills"
          />
          <ToggleRow
            label="Enable Intake Hub list enhancements"
            help="Adds Validation badge + Stuck Reason columns + AI suggestion affordance to the existing list page. OFF = identical to today."
            checked={config.intake_hub_enhancements_enabled}
            onChange={(v) =>
              update(['intake_hub_enhancements_enabled'], v)
            }
            testid="wf-config-intake_hub_enhancements_enabled"
          />
          <ToggleRow
            label="Auto-assign bills to properties / units"
            help="Master switch for the auto-assignment validation rule below. When OFF, every bill goes to manual queue."
            checked={config.auto_assign_bills_to_properties}
            onChange={(v) =>
              update(['auto_assign_bills_to_properties'], v)
            }
            testid="wf-config-auto_assign_bills_to_properties"
          />
        </CardContent>
      </Card>

      {/* ─────── Payment Files card ─────── */}
      <Card className="rounded-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarClock className="h-4 w-4" /> Payment Files
          </CardTitle>
          <CardDescription className="text-xs">
            Scheduled payment-file generation cadence (only used when enabled).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleRow
            label="Payment File schedule enabled"
            help="Master switch for the scheduler. When OFF, payment files are generated on demand only."
            checked={config.payment_file_schedule_enabled}
            onChange={(v) => update(['payment_file_schedule_enabled'], v)}
            testid="wf-config-payment_file_schedule_enabled"
          />

          {config.payment_file_schedule_enabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div>
                <Label className="text-sm">Cadence</Label>
                <Select
                  value={sched.cadence || 'weekly'}
                  onValueChange={(v) =>
                    update(['payment_file_schedule', 'cadence'], v)
                  }
                >
                  <SelectTrigger
                    className="rounded-none mt-1"
                    data-testid="wf-config-cadence"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CADENCES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm">Day of week</Label>
                <Select
                  value={sched.day_of_week || 'friday'}
                  onValueChange={(v) =>
                    update(['payment_file_schedule', 'day_of_week'], v)
                  }
                >
                  <SelectTrigger
                    className="rounded-none mt-1"
                    data-testid="wf-config-day_of_week"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS.map((d) => (
                      <SelectItem key={d} value={d}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm">Time of day (HH:MM)</Label>
                <Input
                  className="rounded-none mt-1"
                  value={sched.time_of_day || '15:00'}
                  onChange={(e) =>
                    update(
                      ['payment_file_schedule', 'time_of_day'],
                      e.target.value
                    )
                  }
                  placeholder="15:00"
                  data-testid="wf-config-time_of_day"
                />
              </div>

              <div>
                <Label className="text-sm">Timezone</Label>
                <Select
                  value={sched.timezone || 'America/Chicago'}
                  onValueChange={(v) =>
                    update(['payment_file_schedule', 'timezone'], v)
                  }
                >
                  <SelectTrigger
                    className="rounded-none mt-1"
                    data-testid="wf-config-timezone"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm">Min bills required</Label>
                <Input
                  type="number"
                  className="rounded-none mt-1"
                  value={sched.min_bills ?? 1}
                  onChange={(e) =>
                    update(
                      ['payment_file_schedule', 'min_bills'],
                      Number(e.target.value)
                    )
                  }
                  data-testid="wf-config-min_bills"
                />
              </div>

              <div>
                <Label className="text-sm">Group by</Label>
                <Select
                  value={sched.group_by || 'property'}
                  onValueChange={(v) =>
                    update(['payment_file_schedule', 'group_by'], v)
                  }
                >
                  <SelectTrigger
                    className="rounded-none mt-1"
                    data-testid="wf-config-group_by"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GROUP_BY.map((g) => (
                      <SelectItem key={g} value={g}>
                        {g}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {sched.cadence === 'custom_cron' && (
                <div className="md:col-span-2">
                  <Label className="text-sm">Custom cron expression</Label>
                  <Input
                    className="rounded-none mt-1 font-mono"
                    value={sched.custom_cron || ''}
                    onChange={(e) =>
                      update(
                        ['payment_file_schedule', 'custom_cron'],
                        e.target.value
                      )
                    }
                    placeholder="0 15 * * 5"
                    data-testid="wf-config-custom_cron"
                  />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─────── Validations card (DYNAMIC) ─────── */}
      <Card className="rounded-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Validations
          </CardTitle>
          <CardDescription className="text-xs">
            Bill-intake validations run for this org. Adding a new validation
            on the backend automatically appears here — fully extensible.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {sortedRules.map((rule, idx) => {
            const block =
              config.validations?.[rule.key] || {
                enabled: rule.default_enabled,
                params: {},
              };
            return (
              <div key={rule.key} data-testid={`wf-rule-${rule.key}`}>
                {idx > 0 && <Separator className="mb-4" />}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{rule.label}</span>
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground border border-border px-1.5 py-0.5">
                        {rule.category}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {rule.description}
                    </p>
                  </div>
                  <Switch
                    checked={!!block.enabled}
                    onCheckedChange={(v) => updateRule(rule.key, 'enabled', v)}
                    data-testid={`wf-rule-${rule.key}-enabled`}
                  />
                </div>
                {block.enabled && rule.params?.length > 0 && (
                  <div className="mt-3 pl-3 border-l-2 border-primary/30 space-y-2">
                    {rule.params.map((p) => (
                      <ParamControl
                        key={p.key}
                        ruleKey={rule.key}
                        param={p}
                        value={block.params?.[p.key] ?? p.default}
                        onChange={(v) => updateRule(rule.key, p.key, v)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {sortedRules.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No validation rules registered.
            </p>
          )}
        </CardContent>
      </Card>

      {/* ─────── Save ─────── */}
      <div className="flex justify-end gap-2 pt-2">
        <Button
          variant="outline"
          className="rounded-none"
          onClick={onClose}
          data-testid="wf-config-cancel"
        >
          Cancel
        </Button>
        <Button
          className="rounded-none"
          disabled={!dirty || saving}
          onClick={handleSave}
          data-testid="wf-config-save"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Workflow Config
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
function ToggleRow({ label, help, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <Label className="text-sm">{label}</Label>
        {help && (
          <p className="text-xs text-muted-foreground mt-0.5">{help}</p>
        )}
      </div>
      <Switch
        checked={!!checked}
        onCheckedChange={onChange}
        data-testid={testid}
      />
    </div>
  );
}
